import { Component, Input, signal, inject, OnChanges, SimpleChanges, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SousTraitantService } from '../../../../core/services/sous-traitant.service';
import { ProjectService } from '../../../../core/services/project.service';
import { Project, SousTraitant } from '../../../../core/models/models';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog.component';

@Component({
  selector: 'app-project-sous-traitant',
  standalone: true,
  imports: [CommonModule, MatIconModule, ConfirmDialogComponent],
  templateUrl: './project-sous-traitant.component.html',
  styleUrl: './project-sous-traitant.component.scss'
})
export class ProjectSousTraitantComponent implements OnChanges {
  private sousTraitantService = inject(SousTraitantService);
  private projectService = inject(ProjectService);

  @Input({ required: true }) project!: Project;
  @Input() readonlyMode = false;

  saved = output<Project>();
  error = output<string>();

  isSaving = signal(false);
  successMessage = signal('');
  localErrorMessage = signal('');

  // Sous-traitant dropdown state
  sousTraitantOptions = signal<SousTraitant[]>([]);
  sousTraitantSearch = signal('');
  sousTraitantPage = signal(0);
  sousTraitantTotalPages = signal(0);
  showSousTraitantDropdown = signal(false);
  isLoadingSousTraitants = signal(false);
  selectedSousTraitants = signal<SousTraitant[]>([]);

  // Confirmation dialog state
  showConfirmDelete = signal(false);
  stToDeleteId = signal<number | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project'] && this.project) {
      if (this.project.sousTraitantIds && this.project.sousTraitantIds.length) {
        this.loadSelectedSousTraitants(this.project.sousTraitantIds);
      } else {
        this.selectedSousTraitants.set([]);
      }
    }
  }

  loadSelectedSousTraitants(ids: number[]): void {
    this.selectedSousTraitants.set([]);
    ids.forEach(id => {
      this.sousTraitantService.getSousTraitantById(id).subscribe({
        next: (st) => this.selectedSousTraitants.set([...this.selectedSousTraitants(), st])
      });
    });
  }

  toggleSousTraitantDropdown(): void {
    this.showSousTraitantDropdown.set(!this.showSousTraitantDropdown());
    if (this.showSousTraitantDropdown()) {
      this.resetAndLoadSousTraitants();
    }
  }

  onSousTraitantSearchChange(value: string): void {
    this.sousTraitantSearch.set(value);
    this.resetAndLoadSousTraitants();
  }

  resetAndLoadSousTraitants(): void {
    this.sousTraitantOptions.set([]);
    this.sousTraitantPage.set(0);
    this.sousTraitantTotalPages.set(0);
    this.loadSousTraitants();
  }

  loadSousTraitants(): void {
    if (this.isLoadingSousTraitants()) return;
    this.isLoadingSousTraitants.set(true);
    this.sousTraitantService.getSousTraitants(this.sousTraitantSearch(), this.sousTraitantPage(), 10, 'denomination,asc')
      .subscribe({
        next: (resp: any) => {
          this.sousTraitantOptions.set([...this.sousTraitantOptions(), ...resp.content]);
          this.sousTraitantTotalPages.set(resp.totalPages);
          this.isLoadingSousTraitants.set(false);
        },
        error: () => {
          this.isLoadingSousTraitants.set(false);
        }
      });
  }

  loadMoreSousTraitants(): void {
    if (this.sousTraitantPage() + 1 >= this.sousTraitantTotalPages()) return;
    this.sousTraitantPage.set(this.sousTraitantPage() + 1);
    this.loadSousTraitants();
  }

  selectSousTraitant(st: SousTraitant): void {
    if (this.readonlyMode) {
      this.emitReadonlyMessage();
      return;
    }
    if (this.isProjectLocked()) {
      this.emitLockedMessage();
      return;
    }
    if (this.selectedSousTraitants().some(item => item.id === st.id)) {
      this.showSousTraitantDropdown.set(false);
      return;
    }
    const newSelection = [...this.selectedSousTraitants(), st];
    this.showSousTraitantDropdown.set(false);
    this.saveSousTraitantLinks(
      newSelection.map(s => s.id!),
      'Sous-traitant ajoute avec succes.',
      () => this.selectedSousTraitants.set(newSelection)
    );
  }

  removeSousTraitant(id: number): void {
    if (this.readonlyMode) {
      this.emitReadonlyMessage();
      return;
    }
    if (this.isProjectLocked()) {
      this.emitLockedMessage();
      return;
    }
    this.stToDeleteId.set(id);
    this.showConfirmDelete.set(true);
  }

  confirmRemoveSousTraitant(): void {
    if (this.isProjectLocked()) {
      this.emitLockedMessage();
      this.cancelRemoveSousTraitant();
      return;
    }
    const id = this.stToDeleteId();
    if (id !== null) {
      const newSelection = this.selectedSousTraitants().filter(st => st.id !== id);
      this.saveSousTraitantLinks(
        newSelection.map(s => s.id!),
        'Sous-traitant retire avec succes.',
        () => this.selectedSousTraitants.set(newSelection)
      );
    }
    this.cancelRemoveSousTraitant();
  }

  cancelRemoveSousTraitant(): void {
    this.stToDeleteId.set(null);
    this.showConfirmDelete.set(false);
  }

  private saveSousTraitantLinks(ids: number[], successMessage: string, onSuccess: () => void): void {
    if (!this.project || !this.project.id) return;
    if (this.isProjectLocked()) {
      this.emitLockedMessage();
      return;
    }

    this.isSaving.set(true);
    const updatedProject: Project = { ...this.project, sousTraitantIds: ids };

    this.projectService.updateProject(this.project.id, updatedProject).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        this.localErrorMessage.set('');
        this.successMessage.set(successMessage);
        onSuccess();
        this.saved.emit(res);
        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (err) => {
        this.isSaving.set(false);
        const message = err?.error?.message || err?.error || 'Erreur lors de la mise a jour des sous-traitants';
        this.localErrorMessage.set(message);
        this.error.emit(message);
      }
    });
  }

  isProjectLocked(): boolean {
    return this.project?.metaStatus !== 'DRAFT';
  }

  private emitLockedMessage(): void {
    const message = 'Le projet est verrouille (VALIDE/TERMINE). Les sous-traitants ne peuvent plus etre modifies.';
    this.localErrorMessage.set(message);
    this.error.emit(message);
  }

  private emitReadonlyMessage(): void {
    const message = "Vous n'avez pas les droits pour modifier les sous-traitants du projet.";
    this.localErrorMessage.set(message);
    this.error.emit(message);
  }
}
